import type { ErrorInfo } from 'react';
import posthog from 'posthog-js';

export interface ErrorReportingOptions {
  source: string;
  component?: string;
  errorInfo?: ErrorInfo;
  queryKey?: unknown;
  additionalContext?: Record<string, unknown>;
}

export interface FailedEventOptions {
  eventName: string;
  source: string;
  additionalContext?: Record<string, unknown>;
}

/**
 * Utility function to ensure any value is converted to an Error object
 *
 * @param error - Any value that should be converted to an Error
 * @returns Error object
 */
export const getErrorObject = (error: unknown): Error => {
  if (error instanceof Error) {
    return error;
  }

  if (typeof error === 'string') {
    return new Error(error);
  }

  if (String(error) === '[object Object]') {
    return new Error(JSON.stringify(error));
  }

  return new Error(String(error));
};

/**
 * Centralized error reporting function for PostHog
 *
 * @param error - The error object
 * @param options - Additional context for error reporting
 */
export const captureError = (error: Error, options: ErrorReportingOptions): void => {
  try {
    const errorContext: Record<string, unknown> = {
      source: options.source,
    };

    // Add optional context fields
    if (options.component) {
      errorContext.component = options.component;
    }

    if (options.errorInfo) {
      errorContext.componentStack = options.errorInfo.componentStack || 'unknown';
    }

    if (options.queryKey) {
      errorContext.queryKey = options.queryKey;
    }

    if (options.additionalContext) {
      Object.assign(errorContext, options.additionalContext);
    }

    console.error(`[${options.source}] Error captured:`, error, errorContext);

    posthog.captureException(error, errorContext);
  } catch (reportError) {
    console.error('Failed to report error to PostHog:', reportError);
  }
};

/**
 * Wrapper function for capturing failed events in PostHog
 * Simplifies the process of tracking failed operations
 *
 * @param error - Any value that will be converted to an Error object
 * @param options - Event options including event name and source
 */
export const captureFailedEvent = (error: unknown, options: FailedEventOptions): void => {
  try {
    const errorObject = getErrorObject(error);

    const errorContext: Record<string, unknown> = {
      event: options.eventName,
      source: options.source,
      additionalContext: JSON.stringify(options.additionalContext),
    };

    console.error(`[${options.source}] Failed event captured:`, errorObject, errorContext);

    posthog.captureException(errorObject, errorContext);
  } catch (reportError) {
    console.error('Failed to report failed event to PostHog:', reportError);
  }
};
